import { useQueryClient } from '@tanstack/react-query';
import {
  createLinkedinAccount,
  getAllLinkedinAccounts,
  getCaptcha,
  getCountryCodes,
  getLinkedinAccountById,
  getThirdPartyScreenshot,
  resetLinkedinAccount,
  setThirdPartyText,
  solveCaptcha,
  syncLinkedinAccount,
  thirdPartyClick,
  updateLinkedinAccount,
  updateLinkedinAccountContract,
  verifyLinkedinAccount,
} from 'API/linkedin-accounts';
import { Period } from 'API/payment';
import { getFullLicense } from 'API/license';
import { ErrorMessages, LinkedinTypes, Products } from 'enums';
import { useHasLicense } from './use-has-license';
import type { LinkedLicenseSummary } from 'types/licenses.types';
import {
  AppSyncErrors,
  AppSyncSteps,
  stepOrder,
  SyncProcessingStatuses
} from 'features/co-pilot/linkedin-accounts/app-sync-dialog/app-sync-dialog.types';
import { CountryCode, LinkedinAccount, LinkedinAccountStatuses, contractNamesForPicker, normalizeAccountStatus, wantsContractSelection } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import useErrorStore from 'stores/error-store';
import useLimitReachedStore from 'stores/limit-reached-store';
import { useUserData } from './use-user-data';
import useUserStore from 'stores/user-store';

const useAppSync = (disableInterval: boolean = false) => {
  const { token } = useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const queryClient = useQueryClient();
  const invalidateLinkedinAccountsQuery = () => queryClient.invalidateQueries({ queryKey: ['linkedinAccounts', 'all'] });

  const [allCountryCodes, setAllCountryCodes] = useState<CountryCode[]>([]);

  const [linkedinAccounts, setLinkedinAccounts] = useState<LinkedinAccount[]>([]);
  const [linkedinAccountsLoading, setLinkedinAccountsLoading] = useState(false);
  const [licenseCapacity, setLicenseCapacity] = useState<{ current: number; remaining: number; billingPeriod: Period }>({
    current: 0,
    remaining: 0,
    billingPeriod: Period.NONE,
  });
  const [linkedLicensesMap, setLinkedLicensesMap] = useState<Record<string, LinkedLicenseSummary[]>>({});
  const setUserLicenses = useUserStore((s) => s.setLicenses);
  const userLicenses = useUserStore((s) => s.licenses || []);

  // Hooks must maintain call order; results currently unused.
  void useHasLicense(Products.SAIA_SALES);
  void useHasLicense(Products.SAIA_RECRUITING);

  const [appSyncDialog, setAppSyncDialog] = useState<{
    open: boolean;
    isStarterExperience: boolean;
    /** When true the dialog is being used for the logout flow, not the login flow */
    isLogoutMode: boolean;
    loading: {
      create: boolean;
      sync: boolean;
      verifyPin: boolean;
      getCaptcha: boolean;
      solveCaptcha: boolean;
      getThirdPartyScreenshot: boolean;
      thirdPartyClick: boolean;
      update: boolean;
    };
    syncProcessingStatus: SyncProcessingStatuses;
    error: AppSyncErrors | null;
    activeStepIndex: number;
    selectedLinkedinProduct: LinkedinTypes;
    selectedCountry: OptionType;
    selectedCity: OptionType;
    userName: string;
    password: string;
    linkedinAccount: LinkedinAccount | null;
    verificationCode: string;
    captcha: string | null;
    markerPosition: { x: number; y: number; percentageX: number; percentageY: number } | null;
    fetchCaptchaIntervalId: NodeJS.Timeout | null;
    thirdPartyScreenshot: string | null;
    fetchThirdPartyScreenshotIntervalId: NodeJS.Timeout | null;
    thirdPartyText: string;
    foundContractNames: string[];
    selectedContract: string | null;
    selectedLicenseId: string | null;
  }>({
    open: false,
    isStarterExperience: false,
    isLogoutMode: false,
    activeStepIndex: 0,
    loading: {
      create: false,
      sync: false,
      verifyPin: false,
      getCaptcha: false,
      solveCaptcha: false,
      getThirdPartyScreenshot: false,
      thirdPartyClick: false,
      update: false,
    },
    syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
    error: null,
    selectedLinkedinProduct: LinkedinTypes.LINKEDIN,
    selectedCountry: { label: '', value: '' },
    selectedCity: { label: '', value: '' },
    userName: '',
    password: '',
    linkedinAccount: null,
    verificationCode: '',
    captcha: null,
    markerPosition: null,
    fetchCaptchaIntervalId: null,
    thirdPartyScreenshot: null,
    fetchThirdPartyScreenshotIntervalId: null,
    thirdPartyText: '',
    foundContractNames: [],
    selectedContract: null,
    selectedLicenseId: null,
  });

  const [accountAlreadyExistsDialog, setAccountAlreadyExistsDialog] = useState<{ open: boolean; userEmail: string | null }>({
    open: false,
    userEmail: null,
  });
  const [waitBeforeSyncDialog, setWaitBeforeSyncDialog] = useState<{
    open: boolean;
    waitingTime: { hours: number; minutes: number } | null;
  }>({ open: false, waitingTime: null });
  const [accountAlreadySyncedDialog, setAccountAlreadySyncedDialog] = useState<{ open: boolean }>({ open: false });

  const handleOpenLimitReachedDialog = useLimitReachedStore((state) => state.handleOpenLimitReachedDialog);

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const fetchLinkedinAccounts = async (disableLoading?: boolean) => {
    if (!disableLoading) {
      setLinkedinAccountsLoading(true);
    }

    // Ensure licenses are loaded (needed for capacity/UI gating in Accounts)
    try {
      if (!userLicenses || userLicenses.length === 0) {
        const licRes = await getFullLicense();
        if (!('error' in licRes)) {
          setUserLicenses(licRes);
        }
      }
    } catch {
      // ignore license fetch errors here; UI can still function
    }

    const res = await getAllLinkedinAccounts();

    if (!res.error) {
      const accounts = res.allLinkedInAccounts || [];

      if (!_.isEmpty(accounts)) {
        setLinkedinAccounts(accounts);
      } else {
        setLinkedinAccounts([]);
      }

      setLicenseCapacity({
        current: res.totalAccountsAdded || 0,
        remaining: res.remainingAccountsCount || 0,
        billingPeriod: res.billingPeriod || Period.YEAR,
      });
      setLinkedLicensesMap(res.linkedLicenses || {});
    } else {
      handleOpenErrorDialog(res.error, '/linkedinAccounts/getAllLinkedInAccounts', 'get');
    }
    setLinkedinAccountsLoading(false);
  };

  const fetchCountryCodes = async () => {
    const res = await getCountryCodes();

    if (!res.error) {
      setAllCountryCodes(res.countries);
    } else {
      handleOpenErrorDialog(res.error, '/linkedinAccounts/countrycodes', 'get');
    }
  };

  useEffect(() => {
    fetchLinkedinAccounts();
    fetchCountryCodes();

    if (!disableInterval) {
      const intervalId = setInterval(() => {
        fetchLinkedinAccounts(true);
      }, 5 * 60 * 1000);

      return () => {
        clearInterval(intervalId);
      };
    }

    return;
  }, [token, disableInterval]);

  /**
   * App sync dialog handlers
   */

  const handleOpenAppSyncDialog = (isStarterExperience: boolean) => {
    return () => {
      setAppSyncDialog((prev) => ({ ...prev, open: true, isStarterExperience }));
    };
  };

  const handleOpenAppSyncAndSynchronizeLiAccount = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;
      const selectedLinkedinProduct = linkedinAccount.liProductType;

      setAppSyncDialog((prev) => ({ ...prev, open: true, isStarterExperience: false, linkedinAccount, selectedLinkedinProduct }));

      handleSynchronizeLinkedinAccountFromAppSync(linkedinAccount);
    };
  };

  const handleOpenAppSyncAndVerifyPin = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;

      const selectedLinkedinProduct = linkedinAccount.liProductType;
      const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);
      setAppSyncDialog((prev) => ({
        ...prev,
        open: true,
        isStarterExperience: false,
        activeStepIndex: syncStepIndex,
        syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
        selectedLinkedinProduct,
        linkedinAccount,
        error: null,
      }));
    };
  };

  const handleOpenAppSyncThirdPartyWebsite = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;

      const selectedLinkedinProduct = linkedinAccount.liProductType;
      const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);
      setAppSyncDialog((prev) => ({
        ...prev,
        open: true,
        isStarterExperience: false,
        activeStepIndex: syncStepIndex,
        syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY,
        selectedLinkedinProduct,
        linkedinAccount,
        error: null,
      }));
      fetchThirdPartyScreenshotForAppSync(linkedinAccount._id);
    };
  };

  const handleOpenAppSyncAndSolveCaptcha = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;

      const selectedLinkedinProduct = linkedinAccount.liProductType;
      const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);
      setAppSyncDialog((prev) => ({
        ...prev,
        open: true,
        isStarterExperience: false,
        activeStepIndex: syncStepIndex,
        syncProcessingStatus: SyncProcessingStatuses.CAPTCHA,
        selectedLinkedinProduct,
        linkedinAccount,
        error: null,
      }));
      fetchCaptchaForAppSync(linkedinAccount._id);
    };
  };

  const handleOpenAppSyncAndCorrectAccessData = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;

      const selectedLinkedinProduct = linkedinAccount.liProductType;
      const userName = linkedinAccount.userEmail;
      const { selectedCountry: userCountry, selectedCity: userCity } = getCountryAndCitySelectionFromAccount(linkedinAccount);

      setAppSyncDialog((prev) => ({
        ...prev,
        open: true,
        isStarterExperience: false,
        activeStepIndex: -1,
        selectedLinkedinProduct,
        selectedCountry: userCountry,
        selectedCity: userCity,
        userName,
        linkedinAccount,
        error: AppSyncErrors.WRONG_CREDENTIALS,
      }));
    };
  };

  const handleCloseAppSyncDialog = async () => {
    if (!_.isNil(intervalIdRef.current)) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    const { fetchCaptchaIntervalId, fetchThirdPartyScreenshotIntervalId } = appSyncDialog;

    if (!_.isNil(fetchCaptchaIntervalId)) {
      clearInterval(fetchCaptchaIntervalId);
    }

    if (!_.isNil(fetchThirdPartyScreenshotIntervalId)) {
      clearInterval(fetchThirdPartyScreenshotIntervalId);
    }

    setAppSyncDialog({
      open: false,
      isStarterExperience: false,
      isLogoutMode: false,
      activeStepIndex: 0,
      loading: {
        create: false,
        sync: false,
        verifyPin: false,
        getCaptcha: false,
        solveCaptcha: false,
        getThirdPartyScreenshot: false,
        thirdPartyClick: false,
        update: false,
      },
      syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      error: null,
      selectedLinkedinProduct: LinkedinTypes.LINKEDIN,
      selectedCountry: { label: '', value: '' },
      selectedCity: { label: '', value: '' },
      userName: '',
      password: '',
      linkedinAccount: null,
      verificationCode: '',
      captcha: null,
      markerPosition: null,
      fetchCaptchaIntervalId: null,
      thirdPartyScreenshot: null,
      fetchThirdPartyScreenshotIntervalId: null,
      thirdPartyText: '',
      foundContractNames: [],
      selectedContract: null,
      selectedLicenseId: null,
    });

    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['taskCounts'] });
    await invalidateLinkedinAccountsQuery();
  };

  const increaseActiveStepIndex = () => {
    setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: Math.min(prev.activeStepIndex + 1, stepOrder.length - 1) }));
  };

  const decreaseActiveStepIndex = () => {
    setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: Math.max(prev.activeStepIndex - 1, 0) }));
  };

  const handleAppSyncLinkedinProductChange = (value: string) => {
    return () => {
      setAppSyncDialog((prev) => ({ ...prev, selectedLinkedinProduct: value as LinkedinTypes }));
    };
  };

  const handleAppSyncCountryChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setAppSyncDialog((prev) => ({ ...prev, selectedCountry: newValue, selectedCity: { label: '', value: '' } }));
  };

  const handleAppSyncCityChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setAppSyncDialog((prev) => ({ ...prev, selectedCity: newValue }));
  };

  const handleAppSyncUserNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppSyncDialog((prev) => ({ ...prev, userName: event.target.value }));
  };

  const handleAppSyncPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppSyncDialog((prev) => ({ ...prev, password: event.target.value }));
  };

  const handleAppSyncLicenseSelect = (licenseId: string | null) => {
    setAppSyncDialog((prev) => ({ ...prev, selectedLicenseId: licenseId }));
  };

  const resolveNotSynchronizedError = (account: LinkedinAccount): AppSyncErrors => {
    const readinessReason = account?.automationReadiness?.reason;
    if (readinessReason === 'connectivity_backoff') {
      return AppSyncErrors.CONNECTIVITY_COOLDOWN;
    }

    const statusMessage = String(account?.statusMessage || '').toLowerCase();
    const isConnectivityCooldown =
      statusMessage.includes('connection cooldown active') ||
      statusMessage.includes('connectivity cooldown active') ||
      statusMessage.includes('backoff active') ||
      statusMessage.includes('connectivity unstable');
    return isConnectivityCooldown ? AppSyncErrors.CONNECTIVITY_COOLDOWN : AppSyncErrors.LOGIN_EXPIRED;
  };

  const getCountryAndCitySelectionFromAccount = (linkedinAccount: LinkedinAccount) => {
    const foundCountry = allCountryCodes.find((country) => country.code === linkedinAccount.countryName);
    let selectedCountry: OptionType = { label: '', value: '' };
    let selectedCity: OptionType = { label: '', value: '' };

    if (!_.isNil(foundCountry)) {
      selectedCountry = { label: foundCountry.name, value: foundCountry.code };
      const foundCity = foundCountry.cities.options.find((city) => city.code === linkedinAccount.cityName);

      if (!_.isNil(foundCity)) {
        selectedCity = { label: foundCity.name, value: foundCity.code };
      }
    }

    return { selectedCountry, selectedCity };
  };

  const handleCreateAndSynchronizeLiAccount = async () => {
    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, create: true } }));

    const { selectedLinkedinProduct, selectedCountry, selectedCity, userName, password } = appSyncDialog;

    // Die Lizenz geht mit dem Anlegen zusammen raus. Vorher wurde sie danach in einem
    // eigenen Aufruf verknüpft, dessen Fehlschlag nur ein `console.warn` war — das Konto
    // existierte dann ohne Sitz, und seit dem 18.08.2026 kauft so eines auch noch eine
    // feste IP. Jetzt entscheidet der Server: keine freie Lizenz, kein Konto.
    const res = await createLinkedinAccount(
      selectedCountry.value,
      selectedCity.value,
      userName,
      password,
      selectedLinkedinProduct,
      undefined,
      appSyncDialog.selectedLicenseId || undefined,
    );

    if (!res.error) {
      fetchLinkedinAccounts(true);
      void invalidateLinkedinAccountsQuery();
      setAppSyncDialog((prev) => ({
        ...prev,
        userName: '',
        password: '',
        linkedinAccount: res,
      }));
      handleSynchronizeLinkedinAccountFromAppSync(res);
    } else if (
      res.error.message === 'You are not allowed to use this product!' ||
      res.error.message === 'Not enough credits!' ||
      res.error.message === 'Not enough credits to create an account!'
    ) {
      handleOpenLimitReachedDialog();
    } else if (
      res.error.message === 'No available SAIA license seat — a LinkedIn account requires one'
      || res.error.message === 'This license was taken by another account in the meantime'
      || res.error.message === 'This license is already linked to a LinkedIn account'
      || res.error.message === 'This license is expired or not active yet'
    ) {
      // Das Lizenz-Gate sitzt jetzt im Server. Ohne diesen Zweig landete seine Absage im
      // allgemeinen Fehlerdialog und sähe aus wie eine Störung statt wie eine Auskunft.
      handleOpenLimitReachedDialog();
    } else if (res.error.message === 'Account already exists!') {
      handleOpenAccountAlreadyExistsDialog(userName);
    } else {
      handleOpenErrorDialog(res.error, '/linkedinAccounts/addLinkedInAccount', 'post');
    }

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, create: false } }));
  };

  const handleSynchronizeLinkedinAccountFromAppSync = async (linkedinAccount: LinkedinAccount) => {
    if (_.isNil(linkedinAccount)) return;
    const linkedinAccountId = linkedinAccount._id;

    const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);

    setAppSyncDialog((prev) => ({
      ...prev,
      activeStepIndex: syncStepIndex,
      syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      error: null,
      loading: { ...prev.loading, sync: true },
    }));

    const res = await syncLinkedinAccount(linkedinAccountId);
    if (!res.error) {
      void invalidateLinkedinAccountsQuery();
    }

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, sync: false } }));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountById(linkedinAccountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((account) => (account._id === linkedinAccountId ? { ...account, status: resLinkedinAccount.status } : account))
          );
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          if (wantsContractSelection(resLinkedinAccount.status, resLinkedinAccount.statusMessage)) {
            setAppSyncDialog((prev) => ({
              ...prev,
              syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
              foundContractNames: contractNamesForPicker(resLinkedinAccount),
            }));
            return;
          }

          switch (resLinkedinAccount.status) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(linkedinAccountId);
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: contractNamesForPicker(resLinkedinAccount),
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => {
                const { selectedCountry, selectedCity } = getCountryAndCitySelectionFromAccount(resLinkedinAccount);
                return {
                  ...prev,
                  activeStepIndex: -1,
                  error: AppSyncErrors.WRONG_CREDENTIALS,
                  selectedCountry,
                  selectedCity,
                  userName: prev.userName || resLinkedinAccount.userEmail || '',
                };
              });
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.WRONG_PIN }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TIMED_OUT_CAPTCHA }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.ONBOARDING_INCOMPLETE }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TWO_FA_NOT_ENABLED }));
              break;
            case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: resolveNotSynchronizedError(resLinkedinAccount) }));
              break;
            default:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.UNKNOWN }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          handleOpenErrorDialog(resLinkedinAccount?.error, '/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else if (res.error.message?.includes('Please wait for')) {
      const errorMessage = res.error.message;
      const timeRegex = /Please wait for (\d+):(\d+)h, before trying to login again!/;
      const match = timeRegex.exec(errorMessage);

      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);

        handleOpenWaitBeforeSyncDialog({ hours, minutes });
      }
    } else if (res.error.message === ErrorMessages.ACCOUNT_ALREADY_IN_QUEUE) {
      handleOpenAccountAlreadySyncedDialog();
    } else {
      handleOpenErrorDialog(res.error, '/linkedinAccounts/:id/sync', 'post');
    }
  };

  const handleAppSyncVerificationCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppSyncDialog((prev) => ({ ...prev, verificationCode: event.target.value }));
  };

  const handleVerifyLinkedinAccountFromAppSync = async (mode: 'pin' | 'approved' = 'pin') => {
    const { linkedinAccount, verificationCode } = appSyncDialog;
    const linkedinAccountId = linkedinAccount?._id;
    if (_.isNil(linkedinAccountId)) return;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, verifyPin: true } }));

    const res = await verifyLinkedinAccount(
      linkedinAccountId,
      mode === 'approved' ? { action: 'approved' } : verificationCode
    );
    if (!res.error) {
      void invalidateLinkedinAccountsQuery();
    }

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, verifyPin: false } }));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const pinSubmittedAt = Date.now();
      let leftFirstTwoFa = false;
      setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.PROCESSING, verificationCode: '' }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountById(linkedinAccountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((account) => (account._id === linkedinAccountId ? { ...account, status: resLinkedinAccount.status } : account))
          );
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          if (wantsContractSelection(resLinkedinAccount.status, resLinkedinAccount.statusMessage)) {
            setAppSyncDialog((prev) => ({
              ...prev,
              syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
              foundContractNames: contractNamesForPicker(resLinkedinAccount),
            }));
            return;
          }

          const status = normalizeAccountStatus(resLinkedinAccount.status);
          switch (status) {
            case LinkedinAccountStatuses.PENDING:
              leftFirstTwoFa = true;
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA: {
              const twoFaAt = (() => {
                try {
                  const parsed = JSON.parse(String(resLinkedinAccount.statusMessage || '')) as { timestamp?: string };
                  const t = parsed?.timestamp ? Date.parse(parsed.timestamp) : 0;
                  return Number.isFinite(t) ? t : 0;
                } catch {
                  return 0;
                }
              })();
              // Recruiter/SN ask for a second authenticator code after Consumer.
              // Stay on the spinner only while this is still the PIN we just sent.
              if (leftFirstTwoFa || twoFaAt > pinSubmittedAt) {
                setAppSyncDialog((prev) => ({
                  ...prev,
                  syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
                  verificationCode: '',
                }));
                break;
              }
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            }
            case LinkedinAccountStatuses.CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              fetchCaptchaForAppSync(linkedinAccountId);
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: contractNamesForPicker(resLinkedinAccount),
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => {
                const { selectedCountry, selectedCity } = getCountryAndCitySelectionFromAccount(resLinkedinAccount);
                return {
                  ...prev,
                  activeStepIndex: -1,
                  error: AppSyncErrors.WRONG_CREDENTIALS,
                  selectedCountry,
                  selectedCity,
                  userName: prev.userName || resLinkedinAccount.userEmail || '',
                };
              });
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.WRONG_PIN }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TIMED_OUT_CAPTCHA }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.ONBOARDING_INCOMPLETE }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TWO_FA_NOT_ENABLED }));
              break;
            case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: resolveNotSynchronizedError(resLinkedinAccount) }));
              break;
            default:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.UNKNOWN }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          handleOpenErrorDialog(resLinkedinAccount?.error, '/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      handleOpenErrorDialog(res.error, '/linkedinAccounts/:id/setPin', 'put');
    }
  };

  const fetchCaptchaForAppSync = async (accountId: string) => {
    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, getCaptcha: true } }));
    const res = await getCaptcha(accountId);

    if (!res.error) {
      const captcha = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;

      const { fetchThirdPartyScreenshotIntervalId } = appSyncDialog;

      if (!_.isNil(fetchThirdPartyScreenshotIntervalId)) {
        clearInterval(fetchThirdPartyScreenshotIntervalId);
        setAppSyncDialog((prev) => ({
          ...prev,
          fetchThirdPartyScreenshotIntervalId: null,
        }));
      }

      const intervalId: NodeJS.Timeout = setInterval(() => reFetchCaptchaForAppSync(accountId, intervalId), 2 * 1000);

      setAppSyncDialog((prev) => ({
        ...prev,
        loading: { ...prev.loading, getCaptcha: false },
        captcha,
        markerPosition: null,
        fetchCaptchaIntervalId: intervalId,
      }));
    } else {
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, getCaptcha: false } }));
      handleOpenErrorDialog(res?.error, '/linkedinAccounts/:id/getCaptcha', 'get');
    }
  };

  const reFetchCaptchaForAppSync = async (accountId: string, intervalId: any) => {
    const res = await getCaptcha(accountId);

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const captcha = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;
      setAppSyncDialog((prev) => ({ ...prev, captcha }));
    } else {
      if (!_.isNil(intervalId)) {
        clearInterval(intervalId);
      }

      setAppSyncDialog((prev) => ({
        ...prev,
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
        captcha: null,
        markerPosition: null,
        fetchCaptchaIntervalId: null,
      }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountById(accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((account) => (account._id === accountId ? { ...account, status: resLinkedinAccount.status } : account))
          );
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (resLinkedinAccount.status) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: contractNamesForPicker(resLinkedinAccount),
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                ...getCountryAndCitySelectionFromAccount(resLinkedinAccount),
                userName: prev.userName || resLinkedinAccount.userEmail || '',
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
              }));
              break;
            case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: resolveNotSynchronizedError(resLinkedinAccount),
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
              }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          handleOpenErrorDialog(resLinkedinAccount?.error, '/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    }
  };

  const handleImageClickFromAppSync = (event: React.MouseEvent) => {
    const image = event.currentTarget;
    const { top, left, width, height } = image.getBoundingClientRect();
    const { clientX, clientY } = event;
    const x = clientX - left;
    const y = clientY - top;

    const percentageX = x / width;
    const percentageY = y / height;

    setAppSyncDialog((prev) => ({ ...prev, markerPosition: { x, y, percentageX, percentageY } }));
  };

  const handleDeleteMarkerFromAppSync = (event: React.MouseEvent) => {
    event.preventDefault();
    setAppSyncDialog((prev) => ({ ...prev, markerPosition: null }));
  };

  const handleSolveCaptchaFromAppSync = async () => {
    const { linkedinAccount, markerPosition, fetchCaptchaIntervalId } = appSyncDialog;

    if (_.isNil(linkedinAccount) || _.isNil(markerPosition)) return;

    const accountId = linkedinAccount._id;
    const positions = { y: markerPosition.percentageY, x: markerPosition.percentageX };

    if (!_.isNil(fetchCaptchaIntervalId)) {
      clearInterval(fetchCaptchaIntervalId);
    }

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: true }, fetchCaptchaIntervalId: null }));

    const res = await solveCaptcha(accountId, positions);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountById(accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((account) => (account._id === accountId ? { ...account, status: resLinkedinAccount.status } : account))
          );
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (resLinkedinAccount.status) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: contractNamesForPicker(resLinkedinAccount),
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                loading: { ...prev.loading, solveCaptcha: false },
                ...getCountryAndCitySelectionFromAccount(resLinkedinAccount),
                userName: prev.userName || resLinkedinAccount.userEmail || '',
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: resolveNotSynchronizedError(resLinkedinAccount),
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
          handleOpenErrorDialog(resLinkedinAccount?.error, '/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
      handleOpenErrorDialog(res?.error, '/linkedinAccounts/:id/solveCaptcha', 'put');
    }
  };

  const handleSelectContractFromAppSync = (contract: string) => {
    return () => {
      setAppSyncDialog((prev) => ({ ...prev, selectedContract: contract }));
    };
  };

  const handleAddContractFromAppSync = async () => {
    const { linkedinAccount, selectedContract } = appSyncDialog;

    if (_.isNil(linkedinAccount)) return;
    const accountId = linkedinAccount._id;
    if (_.isNil(selectedContract)) return;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, update: true } }));

    const res = await updateLinkedinAccountContract(accountId, selectedContract);
    if (!res.error) {
      void invalidateLinkedinAccountsQuery();
    }

    const checkInterval = 1 * 1000;

    if (!res.error) {
      setAppSyncDialog((prev) => ({
        ...prev,
        loading: { ...prev.loading, update: false },
        selectedContract: '',
        foundContractNames: [],
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountById(accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((account) => (account._id === accountId ? { ...account, status: resLinkedinAccount.status } : account))
          );
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (resLinkedinAccount.status) {
            case LinkedinAccountStatuses.PENDING:
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                ...getCountryAndCitySelectionFromAccount(resLinkedinAccount),
                userName: prev.userName || resLinkedinAccount.userEmail || '',
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
              }));
              break;
            case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: resolveNotSynchronizedError(resLinkedinAccount),
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
              }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          handleOpenErrorDialog(resLinkedinAccount?.error, '/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, update: false } }));
      handleOpenErrorDialog(res.error, '/linkedinAccounts/:id', 'put');
    }
  };

  const handleEditAndSynchronizeLiAccount = async () => {
    const { linkedinAccount, selectedCountry, selectedCity, userName, password, selectedLinkedinProduct } = appSyncDialog;

    if (_.isNil(linkedinAccount)) return;
    const accountId = linkedinAccount._id;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, update: true } }));

    const res = await updateLinkedinAccount(accountId, selectedCountry.value, selectedCity.value, userName, password, selectedLinkedinProduct);
    if (!res.error) {
      void invalidateLinkedinAccountsQuery();
    }

    if (!res.error) {
      setAppSyncDialog((prev) => ({
        ...prev,
        loading: { ...prev.loading, update: false },
      }));
      handleSynchronizeLinkedinAccountFromAppSync(linkedinAccount);
    } else {
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, update: false } }));
      handleOpenErrorDialog(res.error, '/linkedinAccounts/:id', 'put');
    }
  };

  const handleReSynchronizeLiAccount = () => {
    const { linkedinAccount } = appSyncDialog;

    if (_.isNil(linkedinAccount)) return;

    handleSynchronizeLinkedinAccountFromAppSync(linkedinAccount);
  };

  const handleResetLinkedinAccount = async () => {
    const { linkedinAccount } = appSyncDialog;
    if (_.isNil(linkedinAccount)) return;

    const res = await resetLinkedinAccount(linkedinAccount._id);
    if (!res.error) {
      // Refresh the account state and close the dialog so user can re-open
      await fetchLinkedinAccounts(true);
      await invalidateLinkedinAccountsQuery();
      handleCloseAppSyncDialog();
    } else {
      handleOpenErrorDialog(res.error, '/linkedinAccounts/:id/reset', 'post');
    }
  };

  const fetchThirdPartyScreenshotForAppSync = async (accountId: string) => {
    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, getThirdPartyScreenshot: true } }));
    const res = await getThirdPartyScreenshot(accountId);

    if (!res.error) {
      const thirdPartyScreenshot = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;

      const { fetchCaptchaIntervalId } = appSyncDialog;

      if (!_.isNil(fetchCaptchaIntervalId)) {
        clearInterval(fetchCaptchaIntervalId);
        setAppSyncDialog((prev) => ({
          ...prev,
          fetchCaptchaIntervalId: null,
        }));
      }

      const intervalId: NodeJS.Timeout = setInterval(() => reFetchThirdPartyScreenshotForAppSync(accountId, intervalId), 2 * 1000);

      setAppSyncDialog((prev) => ({
        ...prev,
        loading: { ...prev.loading, getThirdPartyScreenshot: false },
        thirdPartyScreenshot,
        markerPosition: null,
        fetchThirdPartyScreenshotIntervalId: intervalId,
      }));
    } else {
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, getThirdPartyScreenshot: false } }));
      handleOpenErrorDialog(res?.error, '/linkedinAccounts/:id/getThirdPartyScreenshot', 'get');
    }
  };

  const reFetchThirdPartyScreenshotForAppSync = async (accountId: string, intervalId: any) => {
    const res = await getThirdPartyScreenshot(accountId);

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const thirdPartyScreenshot = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;
      setAppSyncDialog((prev) => ({ ...prev, thirdPartyScreenshot }));
    } else {
      if (!_.isNil(intervalId)) {
        clearInterval(intervalId);
      }

      setAppSyncDialog((prev) => ({
        ...prev,
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
        thirdPartyScreenshot: null,
        markerPosition: null,
        fetchThirdPartyScreenshotIntervalId: null,
      }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountById(accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((account) => (account._id === accountId ? { ...account, status: resLinkedinAccount.status } : account))
          );
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (resLinkedinAccount.status) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: contractNamesForPicker(resLinkedinAccount),
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                ...getCountryAndCitySelectionFromAccount(resLinkedinAccount),
                userName: prev.userName || resLinkedinAccount.userEmail || '',
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
              }));
              break;
            case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: resolveNotSynchronizedError(resLinkedinAccount),
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
              }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          handleOpenErrorDialog(resLinkedinAccount?.error, '/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    }
  };

  const handleThirdPartyClickFromAppSync = async () => {
    const { linkedinAccount, markerPosition, fetchThirdPartyScreenshotIntervalId } = appSyncDialog;

    if (_.isNil(linkedinAccount) || _.isNil(markerPosition)) return;

    const accountId = linkedinAccount._id;
    const positions = { y: markerPosition.percentageY, x: markerPosition.percentageX };

    if (!_.isNil(fetchThirdPartyScreenshotIntervalId)) {
      clearInterval(fetchThirdPartyScreenshotIntervalId);
    }

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: true }, fetchThirdPartyScreenshotIntervalId: null }));

    const res = await thirdPartyClick(accountId, positions);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountById(accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((account) => (account._id === accountId ? { ...account, status: resLinkedinAccount.status } : account))
          );
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (resLinkedinAccount.status) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: contractNamesForPicker(resLinkedinAccount),
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                loading: { ...prev.loading, thirdPartyClick: false },
                ...getCountryAndCitySelectionFromAccount(resLinkedinAccount),
                userName: prev.userName || resLinkedinAccount.userEmail || '',
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: resolveNotSynchronizedError(resLinkedinAccount),
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
          handleOpenErrorDialog(resLinkedinAccount?.error, '/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
      handleOpenErrorDialog(res?.error, '/linkedinAccounts/:id/thirdPartyClick', 'put');
    }
  };

  const handleThirdPartyTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppSyncDialog((prev) => ({ ...prev, thirdPartyText: event.target.value }));
  };

  const handleSetThirdPartyTextFromAppSync = async () => {
    const { linkedinAccount, thirdPartyText } = appSyncDialog;

    if (_.isNil(linkedinAccount)) return;

    const accountId = linkedinAccount._id;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: true } }));

    const res = await setThirdPartyText(accountId, thirdPartyText);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      setAppSyncDialog((prev) => ({ ...prev, thirdPartyText: '' }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountById(accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((account) => (account._id === accountId ? { ...account, status: resLinkedinAccount.status } : account))
          );
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (resLinkedinAccount.status) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: contractNamesForPicker(resLinkedinAccount),
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                loading: { ...prev.loading, thirdPartyClick: false },
                ...getCountryAndCitySelectionFromAccount(resLinkedinAccount),
                userName: prev.userName || resLinkedinAccount.userEmail || '',
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: resolveNotSynchronizedError(resLinkedinAccount),
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
          handleOpenErrorDialog(resLinkedinAccount?.error, '/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
      handleOpenErrorDialog(res?.error, '/linkedinAccounts/:id/thirdPartyClick', 'put');
    }
  };

  /**
   * Account already exists dialog handlers
   */

  const handleOpenAccountAlreadyExistsDialog = (userEmail: string) => {
    setAccountAlreadyExistsDialog({ open: true, userEmail });
  };

  const handleCloseAccountAlreadyExistsDialog = () => {
    setAccountAlreadyExistsDialog({ open: false, userEmail: null });
  };

  /**
   * Wait before sync dialog handlers
   */

  const handleOpenWaitBeforeSyncDialog = (waitingTime: { hours: number; minutes: number }) => {
    setWaitBeforeSyncDialog({ open: true, waitingTime });
  };

  const handleCloseWaitBeforeSyncDialog = () => {
    setWaitBeforeSyncDialog((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!waitBeforeSyncDialog.open) {
      setTimeout(() => {
        setWaitBeforeSyncDialog((prev) => ({ ...prev, waitingTime: null }));
      }, 225);
    }
  }, [waitBeforeSyncDialog.open]);

  /**
   * Open the AppSyncDialog in logout mode (shows a progress spinner while
   * the browser-based logout is running).  Call handleCloseAppSyncDialog
   * when the logout API call resolves.
   */
  const handleOpenAppSyncForLogout = (linkedinAccount: LinkedinAccount) => {
    const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);
    setAppSyncDialog((prev) => ({
      ...prev,
      open: true,
      isLogoutMode: true,
      isStarterExperience: false,
      linkedinAccount,
      activeStepIndex: syncStepIndex,
      syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      error: null,
    }));
  };

  /**
   * Account already synced dialog handlers
   */

  const handleOpenAccountAlreadySyncedDialog = () => {
    setAccountAlreadySyncedDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseAccountAlreadySyncedDialog = () => {
    setAccountAlreadySyncedDialog({ open: false });
  };

  return {
    // fetch
    fetchLinkedinAccounts,
    fetchCountryCodes,
    setLinkedinAccounts,
    // app sync dialog props
    appSyncDialog,
    allCountryCodes,
    linkedinAccounts,
    linkedinAccountsLoading,
    licenseCapacity,
    linkedLicensesMap,
    handleOpenAppSyncDialog,
    handleOpenAppSyncAndSynchronizeLiAccount,
    handleOpenAppSyncForLogout,
    handleOpenAppSyncAndVerifyPin,
    handleOpenAppSyncThirdPartyWebsite,
    handleOpenAppSyncAndSolveCaptcha,
    handleOpenAppSyncAndCorrectAccessData,
    handleCloseAppSyncDialog,
    increaseActiveStepIndex,
    decreaseActiveStepIndex,
    handleAppSyncLinkedinProductChange,
    handleAppSyncCountryChange,
    handleAppSyncCityChange,
    handleAppSyncUserNameChange,
    handleAppSyncPasswordChange,
    handleAppSyncLicenseSelect,
    handleCreateAndSynchronizeLiAccount,
    handleSynchronizeLinkedinAccountFromAppSync,
    handleAppSyncVerificationCodeChange,
    handleVerifyLinkedinAccountFromAppSync,
    fetchCaptchaForAppSync,
    reFetchCaptchaForAppSync,
    handleImageClickFromAppSync,
    handleDeleteMarkerFromAppSync,
    handleSolveCaptchaFromAppSync,
    handleSelectContractFromAppSync,
    handleAddContractFromAppSync,
    handleEditAndSynchronizeLiAccount,
    handleReSynchronizeLiAccount,
    handleResetLinkedinAccount,
    fetchThirdPartyScreenshotForAppSync,
    reFetchThirdPartyScreenshotForAppSync,
    handleThirdPartyClickFromAppSync,
    handleThirdPartyTextChange,
    handleSetThirdPartyTextFromAppSync,
    // other props
    accountAlreadyExistsDialog,
    handleCloseAccountAlreadyExistsDialog,
    waitBeforeSyncDialog,
    handleCloseWaitBeforeSyncDialog,
    accountAlreadySyncedDialog,
    handleCloseAccountAlreadySyncedDialog,
  };
};

export default useAppSync;
